/**
 * A LEITURA INTEGRAL do edital (Fases 2 e 3 da D10) — EM MEDIÇÃO.
 *
 * O servidor lê o edital inteiro com prova de percurso, cruza cada exigência
 * com todos os atestados pelo texto integral deles e só então CERTIFICA. A
 * frase de cima (`faixa`) é escrita pelo código a partir do que foi conferido
 * — nunca pelo modelo —, e é por isso que ela vem pronta da API em vez de ser
 * montada aqui.
 *
 * Nada deste painel decide aba, score ou recomendação: é a medição que vai
 * decidir a virada (Fase 4). O painel diz isso, porque um "nenhum atestado
 * cobre" aqui, lido como veredito, faria desistir de um certame antes da hora.
 *
 * 404 é a resposta mais comum por ora — ninguém rodou `nectuno-leitura`
 * nesta licitação — e ela não diz nada sobre o edital.
 *
 * O QUE O EDITAL EXIGE vem antes dos números (01/10): das 30 decisões da
 * tela com motivo, 19 foram "sem parceiro ou equipe local", com score de 70
 * a 96 — o acervo cobria, e o que decidiu foi onde e como se executa. A
 * lista é fechada e reconhecida por código nos fatos da leitura; cada item
 * vem com a citação do edital, e nada dela decide.
 *
 * ── DUAS PARTES, EM DUAS ABAS (19/10) ─────────────────────────────────────
 * A cobertura das exigências mora em "Edital e requisitos"; o certificado, as
 * condições e o que foi lido, em "Documentos e evidências" (rastreabilidade).
 */
import { useQuery } from "@tanstack/react-query";
import { api, ErroApi, type ExigenciaCruzada } from "../api/cliente";
import { Explicacao } from "./ProvasDeCapacidade";
import css from "./PainelLeituraIntegral.module.css";

const CONDICAO: Record<string, string> = {
  C1_inventario: "inventário",
  C2_extracao: "extração",
  C3_particao: "partição",
  C4_partes: "partes",
  C5_fatos: "fatos",
  C6_alarmes: "alarmes",
  C7_exigencias: "exigências",
  C8_matriz: "matriz",
  C9_encadeamento: "ficha",
};

const COBERTURA: Record<string, string> = {
  total: "coberta por inteiro",
  parcial: "coberta em parte",
  nenhum: "nenhum atestado cobre",
};

/**
 * A regra sobre o documento (referência ao licitante, signatário, local e
 * prazo que o atestado tem de trazer) que a matriz da prova por atestado NÃO
 * pergunta aos atestados (D18): chega sem cobertura e COM o motivo. Sem
 * cobertura e sem motivo é outra coisa — a matriz ainda não cruzou a leitura.
 * Não perguntada não é "nenhum atestado cobre", e a tela não pode dizer que é.
 */
const naoPerguntada = (e: ExigenciaCruzada) => e.cobertura == null && !!e.porque_nenhum;

/** As sem cobertura primeiro: são elas que mudam a decisão. A que não foi
 *  perguntada não muda nenhuma, e fica por último. */
const ORDEM: Record<string, number> = { nenhum: 0, parcial: 1, total: 2 };
const ordem = (e: ExigenciaCruzada) => (naoPerguntada(e) ? 3 : (ORDEM[e.cobertura ?? ""] ?? -1));

/**
 * A EXIGÊNCIA É UMA LINHA EXPANSÍVEL: em cima, a classificação por escrito e o
 * que o edital pede (a citação inteira, sem corte); embaixo, o detalhe — os
 * atestados que a cobrem, com o trecho de cada um e o que falta, e o motivo de
 * nenhum cobrir. Todas as exigências que o sistema devolve são renderizadas, e
 * as sem cobertura já vêm abertas: são elas que mudam a decisão.
 */
function Exigencia({ e }: { e: ExigenciaCruzada }) {
  const cob = e.cobertura ?? "sem matriz";
  const fora = naoPerguntada(e);
  const temDetalhe = e.atestados.length > 0 || !!e.porque_nenhum;
  return (
    <li className={css.exigencia}>
      <details open={e.cobertura === "nenhum" && !fora}>
        <summary className={css.resumoDaExigencia}>
          <span className={`${css.cobertura} ${css[cob] ?? ""}`}>
            {fora
              ? "não perguntada aos atestados"
              : (COBERTURA[cob] ?? "a matriz ainda não cruzou")}
          </span>
          <span className={css.tipo}>
            {e.tipo.replace("_", " ")} · exigência {e.n}
          </span>
          <span className={css.citacaoLinha}>{e.citacao}</span>
        </summary>
        <div className={css.detalhe}>
          <h4 className={css.subtitulo}>O que o edital pede</h4>
          <blockquote className={css.citacao}>{e.citacao}</blockquote>
          {e.atestados.length > 0 && (
            <>
              <h4 className={css.subtitulo}>Atestados relacionados</h4>
              <ul className={css.atestados}>
                {e.atestados.map((p) => (
                  <li key={p.atestado}>
                    <span>
                      <b>{p.atestado}</b>{" "}
                      {p.cobertura === "total" ? "cobre por inteiro" : "cobre em parte"}
                      {p.reconferido ? " · reconferido" : ""}
                      {p.rebaixado ? <i> — {p.rebaixado}</i> : null}
                    </span>
                    {/* A PROVA à vista (D18): o trecho do atestado, conferido
                        por código no texto dele, e — no par em parte — o que
                        falta. */}
                    <span className={css.prova}>
                      {p.prova ? `“${p.prova}”` : "trecho não gravado"}
                    </span>
                    {/* O que cobre e por quê (04/11); sem ela, a lacuna de sempre. */}
                    {p.explicacao ? (
                      <Explicacao x={p.explicacao} />
                    ) : p.falta && (
                      <span className={css.falta}>
                        <i>Lacuna — falta para cobrir por inteiro:</i> {p.falta}
                      </span>
                    )}
                  </li>
                ))}
                {e.mais_atestados > 0 && (
                  <li className={css.tenue}>
                    e mais {e.mais_atestados} atestado(s) relacionado(s)
                  </li>
                )}
              </ul>
            </>
          )}
          {(e.cobertura === "nenhum" || fora) && e.porque_nenhum && (
            <>
              <h4 className={css.subtitulo}>Por que não há cobertura</h4>
              <p className={css.tenue}>{e.porque_nenhum}</p>
            </>
          )}
          {!temDetalhe && (
            <p className={css.tenue}>Sem atestado relacionado e sem outro detalhe gravado.</p>
          )}
        </div>
      </details>
    </li>
  );
}

export function PainelLeituraIntegral({
  id,
  parte,
}: {
  id: string;
  parte: "cobertura" | "rastreio";
}) {
  // A MESMA consulta para as duas partes (a mesma chave de cache): cada parte
  // a pede uma vez, e a segunda a encontra pronta.
  const q = useQuery({
    queryKey: ["leitura", id],
    queryFn: () => api.leituraDe(id),
    retry: false,
  });

  if (q.isPending) return <p className={css.tenue}>Carregando a leitura integral…</p>;
  if (q.error) {
    if (q.error instanceof ErroApi && q.error.status === 404) {
      return (
        <p className={css.tenue}>
          Ainda não lida por inteiro. A leitura integral está em medição e roda sob
          demanda; a ausência dela não diz nada sobre o edital.
        </p>
      );
    }
    return <p className={css.erro}>Não foi possível carregar a leitura integral.</p>;
  }

  const l = q.data;
  const certificada = l.estado === "certificada" || l.estado === "certificada_com_lacunas";
  const exs = [...l.exigencias_cruzadas].sort((a, b) => ordem(a) - ordem(b) || a.n - b.n);
  // As que a matriz não pergunta aos atestados ficam fora dos quatro números
  // de cima (que são só das cruzadas), e a lista de baixo mostra todas: sem
  // este número, a conta de cima não fecharia com a lista.
  const naoPerguntadas = exs.filter(naoPerguntada).length;

  if (parte === "rastreio") {
    return (
      <div className={css.painel}>
        <p className={css.medicao}>
          Cobertura identificada pela pré-análise, em medição: não decide aba, score nem
          recomendação.
        </p>
        <p className={certificada ? css.faixaOk : css.faixaAviso}>{l.faixa}</p>
        {!l.versao_viva && (
          <p className={css.tenue}>Leitura feita numa versão anterior ({l.versao}).</p>
        )}
        <dl className={css.numeros}>
          <div>
            <dt>documentos</dt>
            <dd>{l.documentos}</dd>
          </div>
          <div>
            <dt>partes lidas</dt>
            <dd>{l.partes}</dd>
          </div>
          <div>
            <dt>fatos</dt>
            <dd>{l.fatos}</dd>
          </div>
          <div>
            <dt>exigências</dt>
            <dd>{l.exigencias}</dd>
          </div>
        </dl>
        <ul className={css.condicoes} aria-label="As condições do certificado">
          {l.condicoes.map((c) => (
            <li key={c.condicao} className={c.ok ? css.ok : css.falha} title={c.detalhe}>
              {CONDICAO[c.condicao] ?? c.condicao}
            </li>
          ))}
        </ul>
        {l.matriz?.delta && <p className={css.tenue}>Na fila da matriz: {l.matriz.delta}.</p>}
      </div>
    );
  }

  return (
    <div className={css.painel}>
      {l.condicoes_do_edital.length > 0 && (
        <section className={css.exige} aria-label="O que o edital exige">
          <p className={css.exigeTitulo}>O que o edital exige de quem disputa e executa</p>
          <ul className={css.exigeLista}>
            {l.condicoes_do_edital.map((c) => (
              <li key={c.codigo} className={css.exigeItem}>
                <span className={css.exigeRotulo}>{c.rotulo}</span>
                {c.citacoes.map((cit) => (
                  <blockquote key={cit} className={css.citacao}>
                    {cit}
                  </blockquote>
                ))}
                {c.mais > 0 && <span className={css.tenue}>e mais {c.mais} no edital</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {l.matriz && (
        <dl className={css.numeros}>
          <div>
            <dt>exigências</dt>
            <dd>{l.matriz.exigencias}</dd>
          </div>
          <div>
            <dt>por inteiro</dt>
            <dd>{l.matriz.total}</dd>
          </div>
          <div>
            <dt>em parte</dt>
            <dd>{l.matriz.parcial}</dd>
          </div>
          <div>
            <dt>sem cobertura</dt>
            <dd>{l.matriz.nenhum}</dd>
          </div>
          {naoPerguntadas > 0 && (
            <div>
              <dt>não perguntadas</dt>
              <dd title="Regra sobre o documento ou papel de habilitação: não é exigência de serviço, e não foi perguntada aos atestados">
                {naoPerguntadas}
              </dd>
            </div>
          )}
          <div>
            <dt>
              {l.matriz.atestados_do_acervo == null
                ? "atestados cruzados"
                : l.matriz.finalistas
                  ? "atestados finalistas"
                  : "atestados compatíveis"}
            </dt>
            <dd
              title={
                l.matriz.atestados_do_acervo == null
                  ? undefined
                  : l.matriz.finalistas
                    ? "Os atestados escolhidos pela ficha, entre todos os do acervo, como os que podem comprovar este edital — e os indicados pela gestão. Só eles são lidos por inteiro no cruzamento."
                    : "Só os atestados de tipo de atividade compatível com o do edital, e os indicados pela gestão (D12)"
              }
            >
              {l.matriz.atestados_cruzados}
              {l.matriz.atestados_do_acervo != null && ` de ${l.matriz.atestados_do_acervo}`}
            </dd>
          </div>
        </dl>
      )}
      <p className={css.tenue}>
        Cobertura identificada pela pré-análise: é diagnóstico, e não altera o score nem a
        recomendação.
      </p>

      {exs.length > 0 ? (
        <ul className={css.exigencias}>
          {exs.map((e) => (
            <Exigencia key={e.n} e={e} />
          ))}
        </ul>
      ) : (
        <p className={css.tenue}>A leitura não devolveu exigências para cruzar com os atestados.</p>
      )}
    </div>
  );
}
